import { useCallback, useEffect, useMemo, useState } from 'react';

import type { AgentToken, UiProject } from '@nightshift/ui-types';

import { Empty, Kicker, Spinner } from '../components/primitives.tsx';
import {
  DEFAULT_MCP_CLIENT,
  PLACEHOLDER_TOKEN,
  mcpClient,
  mcpClients,
  setupPrompt,
  type McpClientId,
} from '../lib/mcp-clients.ts';
import { api, type SessionUser } from '../live/api.ts';
import { useSettingsAutosave } from '../lib/settings-autosave.tsx';
import { ProjectAccessPicker } from '../components/project-access-picker.tsx';
import { useSlice, useStore } from '../live/context.tsx';
import type { LiveState } from '../live/store.ts';

const NO_PROJECTS: UiProject[] = [];
const selectProjectSlug = (state: LiveState) => state.projectSlug;
// The same empty array every time: a fresh `[]` per read never compares equal, so
// useSyncExternalStore sees a new snapshot on every call and re-renders without end.
const selectProjects = (state: LiveState): UiProject[] => state.snapshot?.projects ?? NO_PROJECTS;

async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

export function ConnectPanel({ user }: { user: SessionUser | null }) {
  const store = useStore();
  const selectedProject = useSlice(selectProjectSlug);
  const projects = useSlice(selectProjects);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const [tokens, setTokens] = useState<AgentToken[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('Agent');
  const [permission, setPermission] = useState<'read' | 'write'>('write');
  const [expiresInDays, setExpiresInDays] = useState(90);
  const [allProjects, setAllProjects] = useState(false);
  const [projectSlugs, setProjectSlugs] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [clientId, setClientId] = useState<McpClientId>(DEFAULT_MCP_CLIENT);

  const load = useCallback(async () => {
    if (!user) {
      setTokens([]);
      setError(null);
      return;
    }
    try {
      const listed = await api.agentTokens();
      setTokens(listed.tokens);
      setError(null);
    } catch (caught) {
      setTokens(null);
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (allProjects || projectSlugs.length > 0 || !selectedProject) return;
    setProjectSlugs([selectedProject]);
  }, [allProjects, projectSlugs.length, selectedProject]);

  const live = tokens?.filter((row) => row.revokedAt == null) ?? [];
  const revoked = tokens?.filter((row) => row.revokedAt != null) ?? [];
  const snippetInput = useMemo(
    () => ({ origin, token: revealed ?? PLACEHOLDER_TOKEN, permission }),
    [origin, permission, revealed],
  );

  if (!user) {
    return (
      <div className="connect-panel">
        <Empty
          title="Sign in to mint an agent token."
          body="The skill package at /skills is public. Tokens are per account so actions are attributed."
        />
        <Install origin={origin} clientId={clientId} onClient={setClientId} input={snippetInput} />
      </div>
    );
  }

  if (error && !tokens) {
    return (
      <Empty
        title="Agent tokens could not be read."
        body={error}
        action={
          <button type="button" className="btn" onClick={() => void load()}>
            Try again
          </button>
        }
      />
    );
  }

  if (!tokens) {
    return (
      <div className="connect-panel" style={{ display: 'grid', placeItems: 'center', padding: 'var(--s8)' }}>
        <Spinner size={20} />
      </div>
    );
  }

  const mint = async () => {
    setBusy(true);
    try {
      const minted = await api.mintAgentToken({
        name,
        permission,
        expiresInDays,
        ...(allProjects ? { allProjects: true } : { projects: projectSlugs }),
      });
      setRevealed(minted.token);
      setClientId(DEFAULT_MCP_CLIENT);
      store.toast('good', 'Token minted. Copy the setup prompt now — the token is not shown again.');
      await load();
    } catch (caught) {
      store.toast('bad', caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="connect-panel">
      <p className="sheet-note cfg-provenance">
        Agent tokens authenticate MCP clients. <code>read</code> can query the factory;{' '}
        <code>write</code> can create tickets, comment, start, split, and the other member
        commands. Tick All projects, or a subset — you can change that later without minting
        a new secret. Merge, budget reset, and user admin stay on this dashboard. The
        plaintext is shown once.
      </p>

      {revealed ? (
        <section className="sheet-panel connect-once">
          <header className="panel-head">
            <Kicker>Copy this token now</Kicker>
          </header>
          <div className="sheet-panel-body">
            <p className="sheet-note">It will not be stored in plaintext. If you lose it, mint another and revoke this one.</p>
            <pre className="connect-snippet" tabIndex={0}>
              {revealed}
            </pre>
            <div className="connect-actions">
              <button
                type="button"
                className="btn btn-accent btn-sm"
                onClick={() =>
                  void copyText(setupPrompt({ origin, token: revealed, permission })).then((ok) =>
                    store.toast(ok ? 'good' : 'bad', ok ? 'Setup prompt copied.' : 'Copy failed.'),
                  )
                }
              >
                Copy setup prompt
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => void copyText(revealed).then((ok) => store.toast(ok ? 'good' : 'bad', ok ? 'Token copied.' : 'Copy failed.'))}
              >
                Copy token
              </button>
              <button type="button" className="btn btn-sm" onClick={() => setRevealed(null)}>
                Dismiss
              </button>
            </div>
          </div>
        </section>
      ) : null}

      <section className="sheet-panel">
        <header className="panel-head">
          <Kicker count={live.length}>Tokens</Kicker>
        </header>
        <div className="sheet-panel-body">
          {live.length === 0 ? <p className="sheet-note">No live tokens.</p> : null}
          {live.map((row) => (
            <TokenRow
              key={row.id}
              row={row}
              projects={projects}
              busy={busy}
              onSave={async (scope) => {
                setBusy(true);
                try {
                  await api.patchAgentToken(row.id, scope);
                  await load();
                } finally {
                  setBusy(false);
                }
              }}
              onRevoke={async () => {
                setBusy(true);
                try {
                  await api.revokeAgentToken(row.id);
                  store.toast('good', `${row.name} revoked.`);
                  await load();
                } catch (caught) {
                  store.toast('bad', caught instanceof Error ? caught.message : String(caught));
                } finally {
                  setBusy(false);
                }
              }}
            />
          ))}
          {revoked.length > 0 ? (
            <p className="sheet-note" style={{ marginTop: 'var(--s3)' }}>
              {revoked.length} revoked {revoked.length === 1 ? 'token' : 'tokens'} kept for the audit trail.
            </p>
          ) : null}
        </div>
      </section>

      <section className="sheet-panel">
        <header className="panel-head">
          <Kicker>Mint token</Kicker>
        </header>
        <div className="sheet-panel-body">
          <div className="cfg-grid">
            <label className="cfg-field">
              <span className="cfg-label">Name</span>
              <input
                className="input"
                value={name}
                disabled={busy}
                placeholder="e.g. laptop Codex"
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="cfg-field">
              <span className="cfg-label">Permission</span>
              <div className="seg">
                {(['read', 'write'] as const).map((entry) => (
                  <button key={entry} type="button" aria-pressed={permission === entry} onClick={() => setPermission(entry)}>
                    {entry}
                  </button>
                ))}
              </div>
            </label>
            <div className="cfg-field">
              <span className="cfg-label">Projects</span>
              <ProjectAccessPicker
                projects={projects}
                allProjects={allProjects}
                slugs={projectSlugs}
                disabled={busy}
                onChange={(next) => {
                  setAllProjects(next.allProjects);
                  setProjectSlugs(next.slugs);
                }}
              />
            </div>
            <label className="cfg-field">
              <span className="cfg-label">Expires</span>
              <select
                className="input"
                value={expiresInDays}
                disabled={busy}
                onChange={(e) => setExpiresInDays(Number(e.target.value))}
              >
                <option value={30}>30 days</option>
                <option value={90}>90 days</option>
                <option value={365}>365 days</option>
              </select>
            </label>
          </div>
          <button
            type="button"
            className="btn btn-accent btn-sm"
            disabled={busy || name.trim().length === 0 || (!allProjects && projectSlugs.length === 0)}
            onClick={() => void mint()}
          >
            Generate token
          </button>
        </div>
      </section>

      <Install origin={origin} clientId={clientId} onClient={setClientId} input={snippetInput} />
    </div>
  );
}

function scopeSummary(row: AgentToken): string {
  if (row.allProjects) return 'all projects';
  if (row.projects.length === 0) return 'no projects';
  return row.projects.map((project) => project.name).join(', ');
}

function TokenRow({
  row,
  projects,
  busy,
  onSave,
  onRevoke,
}: {
  row: AgentToken;
  projects: UiProject[];
  busy: boolean;
  onSave: (scope: { allProjects?: boolean; projects?: string[] }) => Promise<void>;
  onRevoke: () => void;
}) {
  const autosave = useSettingsAutosave({
    value: { allProjects: row.allProjects, slugs: row.projects.map((project) => project.slug) },
    save: async (draft) => onSave(draft.allProjects ? { allProjects: true } : { projects: draft.slugs }),
    what: `${row.name} token access`,
    enabled: !busy,
    delay: 500,
    validate: (draft) => draft.allProjects || draft.slugs.length > 0 ? null : 'Select at least one project.',
  });
  const { draft, setDraft, error } = autosave;
  const { allProjects, slugs } = draft;

  return (
    <div className="connect-token" data-token={row.tokenPrefix}>
      <div className="connect-token-head">
        <div style={{ minWidth: 0 }}>
          <span className="cfg-label">
            {row.name}{' '}
            <code className="cfg-path mono">{row.tokenPrefix}…</code>
          </span>
          <span className="cfg-provenance">
            {row.permission}
            {` · ${scopeSummary(row)}`}
            {row.lastUsedAt ? ` · last used ${new Date(row.lastUsedAt).toLocaleString()}` : ' · never used'}
            {row.expiresAt ? ` · expires ${new Date(row.expiresAt).toLocaleDateString()}` : ''}
          </span>
        </div>
        <div className="connect-token-actions">
          <button type="button" className="btn btn-sm" disabled={busy} onClick={onRevoke}>
            Revoke
          </button>
        </div>
      </div>
      <ProjectAccessPicker
        projects={projects}
        allProjects={allProjects}
        slugs={slugs}
        disabled={busy}
        onChange={(next) => {
          setDraft(next);
        }}
      />
      {error ? <p className="cfg-error" role="alert">Unsaved: {error}</p> : null}
    </div>
  );
}

function Install({
  origin,
  clientId,
  onClient,
  input,
}: {
  origin: string;
  clientId: McpClientId;
  onClient: (id: McpClientId) => void;
  input: { origin: string; token: string; permission: 'read' | 'write' };
}) {
  const store = useStore();
  const clients = mcpClients(input);
  const selected = mcpClient(clientId, input);
  const formatLabel = selected.format === 'prompt' ? 'prompt' : selected.format.toUpperCase();

  return (
    <section className="sheet-panel">
      <header className="panel-head">
        <Kicker>Install on an agent</Kicker>
      </header>
      <div className="sheet-panel-body">
        <p className="sheet-note">
          Skill package:{' '}
          <a href="/skills/" className="connect-link">
            {origin}/skills/
          </a>
          . Default is a prompt you paste to the agent. Or copy the config for a specific client
          — Codex and Grok are TOML, not JSON.
        </p>
        <span className="cfg-label">Client</span>
        <div className="connect-clients" aria-label="MCP client">
          {clients.map((entry) => (
            <button
              key={entry.id}
              type="button"
              aria-pressed={clientId === entry.id}
              onClick={() => onClient(entry.id)}
            >
              {entry.label}
            </button>
          ))}
        </div>
        {selected.files.length > 0 ? (
          <p className="sheet-note">
            {formatLabel} · {selected.files.join(' · ')}
          </p>
        ) : (
          <p className="sheet-note">{selected.notes}</p>
        )}
        {selected.files.length > 0 ? <p className="sheet-note">{selected.notes}</p> : null}
        <pre
          className={selected.format === 'prompt' ? 'connect-snippet connect-snippet-text' : 'connect-snippet'}
          tabIndex={0}
        >
          {selected.body}
        </pre>
        <div className="connect-actions">
          <button
            type="button"
            className="btn btn-accent btn-sm"
            onClick={() =>
              void copyText(selected.body).then((ok) =>
                store.toast(
                  ok ? 'good' : 'bad',
                  ok ? `${selected.label} copied.` : 'Copy failed.',
                ),
              )
            }
          >
            Copy {selected.label.toLowerCase()}
          </button>
        </div>
        {selected.command ? (
          <>
            <span className="cfg-label" style={{ marginTop: 'var(--s3)' }}>
              CLI equivalent
            </span>
            <pre className="connect-snippet connect-snippet-text">{selected.command}</pre>
            <div className="connect-actions">
              <button
                type="button"
                className="btn btn-sm"
                onClick={() =>
                  void copyText(selected.command ?? '').then((ok) =>
                    store.toast(ok ? 'good' : 'bad', ok ? 'Command copied.' : 'Copy failed.'),
                  )
                }
              >
                Copy command
              </button>
            </div>
          </>
        ) : null}
      </div>
    </section>
  );
}
